/* eslint-disable react-hooks/exhaustive-deps */
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useMe } from '@orientaction/hooks';
import { getFromLS } from '@orientaction/utils';
import { kebabCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MinorTestCard } from '../../../../shared/components';
import { apiServiceNative } from '../../../../../../utils/fetchNative';
import { getMe, sendMailForMore } from '../../../../../../services/user.service';
import TestLimitModal from '../../../components/TestLimitModal';
import { useRecommendedTestsSectionStyles } from './RecommendedTestsSection.styles';

type RecommendedTestsSectionProps = {
  recommendedTests: any;
  completedTestIds?: number[];
  recommendedTestsOrder?: number[];
};

const RecommendedTestsSection = ({
  recommendedTests = [],
  completedTestIds = [],
  recommendedTestsOrder = [],
}: RecommendedTestsSectionProps) => {
  const classes = useRecommendedTestsSectionStyles();
  const history = useHistory();
  const { me } = useMe();
  const [freeTests, setFreeTests] = useState<number[]>([]);
  const [openingTestId, setOpeningTestId] = useState<number | null>(null);
  const [openUnlockModal, setOpenUnlockModal] = useState(false);

  const token = getFromLS('userToken') || '';

  const allTestsArray = recommendedTests?.data || recommendedTests || [];

  const filteredTestsArray = allTestsArray.filter((test: any) => {
    const attributes = test?.attributes || test;
    return !attributes?.isArchived && !attributes?.isDesabled;
  });

  const testsArray = useMemo(() => {
    if (!recommendedTestsOrder?.length) {
      return filteredTestsArray;
    }

    const testsMap = new Map<number, any>();
    filteredTestsArray.forEach((test: any) => {
      if (test?.id) {
        testsMap.set(test.id, test);
      }
    });

    const processedIds = new Set<number>();
    const orderedTests: any[] = [];

    recommendedTestsOrder.forEach((orderId: number) => {
      const test = testsMap.get(orderId);
      if (test) {
        orderedTests.push(test);
        processedIds.add(orderId);
      }
    });

    filteredTestsArray.forEach((test: any) => {
      if (test?.id && !processedIds.has(test.id)) {
        orderedTests.push(test);
      }
    });

    return orderedTests;
  }, [filteredTestsArray, recommendedTestsOrder]);

  useEffect(() => {
    const getFreeTest = async () => {
      const response = await apiServiceNative.get('/api/free-test?populate=tests');
      const testIds = response.data.attributes.tests.data.map((testItem: any) => testItem.id);
      setFreeTests(testIds);
    };

    if (token) {
      getFreeTest();
    }
  }, [token]);

  const handleDiscoverAll = () => {
    history.push('/user/tests');
  };

  const handleTestAction = async (test: any) => {
    const testId = test?.id;
    const isDone = completedTestIds.includes(testId);

    if (isDone) {
      history.push('/mes-resultats');
      return;
    }

    setOpeningTestId(testId);

    try {
      const dataMe = await getMe(token);
      const freshUser = dataMe.data;
      const minimum = freshUser.beneficiaryType === 'rattached' ? 10 : 3;
      const testsToDoIds = freshUser.tests_to_do?.map((item: any) => item.id) || [];

      if (
        freeTests.includes(testId) ||
        freshUser.counterTest === null ||
        freshUser.counterTest < minimum ||
        freshUser.statusSubscription === 'active' ||
        testsToDoIds.includes(testId)
      ) {
        history.push(`/test/${testId}/${kebabCase(test?.attributes?.title || test?.title || '')}`);
      } else {
        setOpenUnlockModal(true);
      }
    } finally {
      setOpeningTestId(null);
    }
  };

  if (!testsArray.length) {
    return null;
  }

  const sendMail = async () => {
    if (!me?.id) {
      setOpenUnlockModal(false);
      return;
    }

    const resp = await sendMailForMore(me.id, token);
    if (resp) {
      setOpenUnlockModal(false);
    }
  };

  const paySubscription = () => {
    const paymentLink =
      me?.beneficiaryType === 'rattached'
        ? process.env.REACT_APP_PAYMENT_LINK
        : process.env.REACT_APP_PAYMENT_LINK_NO_RATTACHED;

    window.open(
      `${paymentLink}?prefilled_email=${me.email}&locale=fr&client_reference_id=${me.id}`,
      '_blank'
    );
    setOpenUnlockModal(false);
  };

  return (
    <section className={classes.recommendedSection} aria-labelledby="recommended-tests-title">
      <div className={classes.recommendedHeader}>
        <h2 id="recommended-tests-title" className={classes.recommendedTitle}>
          Découvrez les tests qui pourraient vous intéresser
        </h2>
        <button type="button" className={classes.discoverAllButton} onClick={handleDiscoverAll}>
          Découvrez tous les tests
          <ArrowForwardIcon style={{ width: 16, height: 16 }} />
        </button>
      </div>

      <div className={classes.cardGrid}>
        {testsArray.map((test: any) => {
          const attributes = test?.attributes || test;
          const isDone = completedTestIds.includes(test?.id);
          const buttonLabel = isDone ? 'Voir mes résultats' : 'Faire le test';

          return (
            <div key={test.id} className={classes.cardGridItem}>
              <MinorTestCard
                title={attributes?.title || test?.title || 'Test sans titre'}
                image={attributes?.image || test?.image}
                thumbnail={attributes?.thumbnail || test?.thumbnail}
                time={attributes?.time || test?.time}
                status={isDone ? 'done' : 'todo'}
                buttonLabel={buttonLabel}
                showResultIcon={isDone}
                loading={openingTestId === test.id}
                onAction={() => handleTestAction(test)}
              />
            </div>
          );
        })}
      </div>

      <TestLimitModal
        open={openUnlockModal}
        onClose={sendMail}
        onUnlock={paySubscription}
      />
    </section>
  );
};

export default RecommendedTestsSection;
